"use client";
import { useState, useRef, useEffect } from "react";
import CardChevron from "@/Components/Common/CardChevron";
import { useIntl } from "react-intl";
import { Table, Tooltip } from "antd";
import Image from "next/image";
import type { ColumnsType } from "antd/es/table";
import "./LiveTrainScheduleTable.css";

export type TrainStatus = "OnTime" | "Late" | "Cancelled" | "Departed";

export interface TrainRecord {
  key: string;
  from: string;
  to: string;
  originalDeparture?: string;
  departure: string;
  originalArrival?: string;
  arrival: string;
  trainNo: string;
  status: TrainStatus;
  details?: React.ReactNode;
}

interface LiveTrainScheduleTableProps {
  data: TrainRecord[];
  emptyMessage?: string;
  /** True when the backend has more rows than have been loaded so far. */
  hasMore?: boolean;
  /** Fetches the next page from the backend (renders a "Load more" button). */
  onLoadMore?: () => void;
}

type SortOrderType = "ascend" | "descend" | null;

const statusColors: Record<TrainStatus, string> = {
  OnTime: "#039855",
  Late: "#FDB022",
  Cancelled: "#8F011B",
  Departed: "#667085",
};

// Departure / arrival cell: original time struck-through and small on top,
// effective (bold) time below. Cancelled rows show the single time struck.
const renderTimeCell = (
  time: string,
  original: string | undefined,
  status: TrainStatus
) => {
  if (status === "Cancelled") {
    return (
      <div className="ant-table-cell-content">
        <span className="ff-inter ant-table-line-text-cancelled">{time}</span>
      </div>
    );
  }

  const showOriginal = !!original && original !== time;

  return (
    <div className="ant-table-cell-content">
      {showOriginal && (
        <span className="ff-inter ant-table-line-text">{original}</span>
      )}
      <span className={`ff-inter ${showOriginal ? "ant-table-time-effective" : ""}`}>
        {time}
      </span>
    </div>
  );
};

const LiveTrainScheduleTable: React.FC<LiveTrainScheduleTableProps> = ({
  data,
  emptyMessage,
  hasMore,
  onLoadMore,
}) => {
  const intl = useIntl();
  const detailRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());
  const [sortConfig, setSortConfig] = useState<{
    columnKey: string;
    order: SortOrderType;
  }>({ columnKey: "", order: null });

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const clickedInside = Object.values(detailRefs.current).some(
        (el) => el && el.contains(e.target as Node)
      );
      if (!clickedInside) setVisibleKeys(new Set());
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleSort = (columnKey: string) => {
    setSortConfig((prev) => {
      if (prev.columnKey === columnKey) {
        const nextOrder =
          prev.order === "ascend"
            ? "descend"
            : prev.order === "descend"
            ? null
            : "ascend";
        return { columnKey, order: nextOrder };
      }
      return { columnKey, order: "ascend" };
    });
  };

  const renderSortIcon = (columnKey: string) => {
    const isActive =
      sortConfig.columnKey === columnKey && sortConfig.order !== null;

    return (
      <Image
        src="../../assets/table_icons/sort-vertical.svg"
        alt="Sort Icon"
        width={12}
        height={12}
        className={`${isActive ? "sort-icon-active" : "sort-icon"}`}
      />
    );
  };

  const columns: ColumnsType<TrainRecord> = [
    {
      title: (
        <div className="ff-inter">{intl.formatMessage({ id: "from" })}</div>
      ),
      dataIndex: "from",
      key: "from",
    },
    {
      title: <div className="ff-inter">{intl.formatMessage({ id: "to" })}</div>,
      dataIndex: "to",
      key: "to",
    },
    {
      title: (
        <div
          className="ant-table-sort-title ff-inter"
          onClick={() => handleSort("departure")}
        >
          {intl.formatMessage({ id: "departure" })}{" "}
          {renderSortIcon("departure")}
        </div>
      ),
      dataIndex: "departure",
      key: "departure",
      sorter: (a, b) => a.departure.localeCompare(b.departure),
      sortOrder: sortConfig.columnKey === "departure" ? sortConfig.order : null,
      showSorterTooltip: false,
      render: (departure: string, record: TrainRecord) =>
        renderTimeCell(departure, record.originalDeparture, record.status),
    },
    {
      title: (
        <div
          className="ant-table-sort-title ff-inter"
          onClick={() => handleSort("arrival")}
        >
          {intl.formatMessage({ id: "arrival" })} {renderSortIcon("arrival")}
        </div>
      ),
      dataIndex: "arrival",
      key: "arrival",
      sorter: (a, b) => a.arrival.localeCompare(b.arrival),
      sortOrder: sortConfig.columnKey === "arrival" ? sortConfig.order : null,
      showSorterTooltip: false,
      render: (arrival: string, record: TrainRecord) =>
        renderTimeCell(arrival, record.originalArrival, record.status),
    },
    {
      title: (
        <div
          className="ant-table-sort-title ff-inter"
          onClick={() => handleSort("trainNo")}
        >
          {intl.formatMessage({ id: "scheduleTrainNo" })}.{" "}
          {renderSortIcon("trainNo")}
        </div>
      ),
      dataIndex: "trainNo",
      key: "trainNo",
      sorter: (a, b) => a.trainNo.localeCompare(b.trainNo),
      sortOrder: sortConfig.columnKey === "trainNo" ? sortConfig.order : null,
      showSorterTooltip: false,
    },
    {
      title: (
        <div
          className="ant-table-sort-title ff-inter"
          onClick={() => handleSort("status")}
        >
          {intl.formatMessage({ id: "scheduleStatus" })}{" "}
          {renderSortIcon("status")}
        </div>
      ),
      key: "status",
      render: (_: any, record: TrainRecord) => {
        const isVisible = visibleKeys.has(record.key);

        return (
          <div className="status-cell-container">
            <div
              className="status-cell"
              style={{
                backgroundColor: statusColors[record.status],
              }}
            >
              {intl.formatMessage({ id: `schedule${record.status}` })}
            </div>

            {/* {record.details && (
              <Tooltip
                title={
                  <div className="toolTip">
                    <div className="tooltip-content ff-inter">
                      {record.details}
                    </div>
                  </div>
                }
                placement="bottomRight"
                arrow={true}
              >
                <div className="details-btn-container">
                  <span className="details-btn-text">
                    {intl.formatMessage({ id: "details" })}
                  </span>
                  <CardChevron direction="down" width={10} height={6} />
                </div>
              </Tooltip>
            )} */}
          </div>
        );
      },
      sorter: (a, b) => a.status.localeCompare(b.status),
      sortOrder: sortConfig.columnKey === "status" ? sortConfig.order : null,
      showSorterTooltip: false,
      width: 180,
      align: "left",
    },
  ];

  // "Load more" — same style as the train search results: a centered, clickable
  // label with a chevron. Each click asks the backend for the next page.
  const loadMoreButton =
    hasMore && onLoadMore ? (
      <div className="load-btn">
        <h2
          onClick={onLoadMore}
          className="cursor-pointer d-inline-flex align-items-center"
        >
          {intl.formatMessage({ id: "loadMoreResults" })}
          <CardChevron direction="down" width={15} height={15} />
        </h2>
      </div>
    ) : null;

  if (data.length === 0) {
    return (
      <>
        {emptyMessage ? (
          <div className="empty-message-container">
            <Image
              src="/assets/booking/red-train.svg"
              alt="No Trains"
              width={48}
              height={48}
              style={{ marginBottom: 16 }}
            />
            <div className="empty-message-text ff-inter">{emptyMessage}</div>
          </div>
        ) : (
          <div className="empty-message-container"></div>
        )}
        {loadMoreButton}
      </>
    );
  }

  return (
    <div className="live-train-schedule-table-wrapper">
      <Table
        columns={columns}
        dataSource={data}
        pagination={false}
        className="responsive-train-table ff-inter"
        scroll={{ x: "max-content" }}
      />
      {loadMoreButton}
    </div>
  );
};

export default LiveTrainScheduleTable;
